/**
 * <mobile-menu-drawer>
 * Light-DOM wrapper around the native <dialog> used as the mobile menu drawer.
 * The dialog itself provides focus trapping, Esc-to-close and an inert
 * background for free (showModal). This component only wires the opener
 * (which lives in the header bar, outside this element) and the close
 * button, and keeps scroll-lock/aria-expanded/focus-return in sync.
 */
class MobileMenuDrawer extends HTMLElement {
  constructor () {
    super()
    this.dialog = null
    this.opener = null
    this.closeButton = null
    this.desktopQuery = null

    this.handleOpenClick = this.handleOpenClick.bind(this)
    this.handleCloseClick = this.handleCloseClick.bind(this)
    this.handleDialogClose = this.handleDialogClose.bind(this)
    this.handleDesktopChange = this.handleDesktopChange.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  connectedCallback () {
    this.dialog = this.querySelector('dialog')
    this.opener = document.querySelector('[data-menu-open]')
    this.closeButton = this.querySelector('[data-menu-close]')

    if (!this.dialog) { return }

    if (this.opener) {
      this.opener.addEventListener('click', this.handleOpenClick)
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.handleCloseClick)
    }

    this.dialog.addEventListener('close', this.handleDialogClose)
    // The UA already closes a modal dialog on Esc; this fallback keeps the
    // behaviour explicit (and testable) where that close-watcher isn't run.
    this.dialog.addEventListener('keydown', this.handleKeydown)

    this.desktopQuery = window.matchMedia('(min-width: 1200px)')
    this.desktopQuery.addEventListener('change', this.handleDesktopChange)
  }

  disconnectedCallback () {
    if (this.opener) {
      this.opener.removeEventListener('click', this.handleOpenClick)
    }

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.handleCloseClick)
    }

    if (this.dialog) {
      this.dialog.removeEventListener('close', this.handleDialogClose)
      this.dialog.removeEventListener('keydown', this.handleKeydown)
    }

    if (this.desktopQuery) {
      this.desktopQuery.removeEventListener('change', this.handleDesktopChange)
    }
  }

  open () {
    if (!this.dialog || this.dialog.open) { return }

    this.dialog.showModal()

    if (this.opener) {
      this.opener.setAttribute('aria-expanded', 'true')
    }
    // Body scroll lock comes from critical.css:
    // html:has(dialog[scroll-lock][open]) { overflow: hidden }
  }

  close () {
    if (!this.dialog || !this.dialog.open) { return }

    this.dialog.close()
  }

  handleOpenClick () {
    this.open()
  }

  handleCloseClick () {
    this.close()
  }

  handleDialogClose () {
    if (!this.opener) { return }

    this.opener.setAttribute('aria-expanded', 'false')

    // The burger is display:none once the viewport crosses into desktop (the
    // drawer auto-closes there) - focusing a hidden element is a silent no-op,
    // so fall back to the first visible header control.
    if (this.opener.offsetParent !== null) {
      this.opener.focus()
      return
    }

    const fallback = document.querySelector('.c-header__nav-link, .c-header__logo--desktop')
    if (fallback) { fallback.focus() }
  }

  handleDesktopChange (event) {
    if (event.matches) {
      this.close()
    }
  }

  handleKeydown (event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
    }
  }
}

customElements.define('mobile-menu-drawer', MobileMenuDrawer)
